import { createContext, useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { useSelector } from 'react-redux';
import { getCookie } from 'cookies-next';
import { updateRecordInCollection } from 'lib/helpers';

import { selectCurrentUser } from 'features/auth/slice';

import {
  recordsMatch,
  Socket,
  useSocket,
} from '@frontendmentorio/design-system-v2';

import useFetch from 'lib/hooks/useFetch';
import useFetchConfig from 'lib/hooks/useFetchConfig';

const { origin } = new URL(process.env.NEXT_PUBLIC_REST_API_URL);

Socket.init({
  debug: process.env.APP_ENV !== 'production',
  url: origin,
});

export const ConversationContext = createContext();

export const ConversationProvider = ({ children }) => {
  const conversationsSocket = useSocket('/socket/v1/conversations');
  const currentUser = useSelector(selectCurrentUser);

  const params = useParams();
  const recipientId = params?.recipientId?.[0];
  const { cache, mutate } = useFetchConfig();

  const {
    data: { data: conversations },
    isLoading,
  } = useFetch(currentUser ? '/api/v3/conversations' : null);

  const {
    data: { data: messages },
  } = useFetch(
    recipientId ? `/api/v3/conversations/${recipientId}/messages` : null
  );

  const getConversationFor = (userId) => {
    if (!userId) return null;
    return conversations?.find((record) => recordsMatch(record.with, userId));
  };

  const refetchConversations = async () => {
    const res = await fetch('/api/v3/conversations');
    if (!res.ok) return;

    const data = await res.json();
    await mutate(`/api/v3/conversations`, data, { revalidate: false });
  };

  const activeConversation = getConversationFor(recipientId);
  const notFound = !isLoading && !activeConversation;

  const {
    data: { data: recipient },
  } = useFetch(
    recipientId && notFound ? `/api/v3/talent/${recipientId}` : null
  );

  const mutateConversationFor = (userId, callback) => {
    mutate(
      '/api/v3/conversations',
      async ({ data, ...rest }) => ({
        data: updateRecordInCollection(
          data,
          (record) => recordsMatch(record.with, userId),
          callback
        ),
        ...rest,
      }),
      { revalidate: false }
    );
  };

  const appendMessageFor = (userId, message) => {
    if (!cache.has(`/api/v3/conversations/${userId}/messages`)) {
      return refetchConversations();
    }

    mutate(
      `/api/v3/conversations/${userId}/messages`,
      async ({ data = [], ...rest } = {}) => ({
        data: [...data, message],
        ...rest,
      }),
      { revalidate: false }
    );
  };

  const markAsRead = async (userId) => {
    if (!userId) return;

    const conversation = getConversationFor(userId);

    if (!conversation || !conversation.lastResponse) return;
    if (conversation.lastResponse.isRead) return;

    await fetch(`/api/v3/conversations/${userId}/mark-as-read`, {
      method: 'POST',
    });

    mutateConversationFor(userId, (record) => ({
      ...record,
      lastResponse: { ...record.lastResponse, isRead: true },
    }));
  };

  const sendMessageFor = async (userId, formData) => {
    const res = await fetch(`/api/v3/conversations/${userId}/messages`, {
      body: JSON.stringify(formData),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });

    const { data: message } = await res.json();

    appendMessageFor(userId, message);
  };

  const unreadCount = useMemo(() => {
    return conversations?.reduce((memo, conversation) => {
      if (!conversation.lastResponse) return memo;

      return memo + !conversation.lastResponse.isRead;
    }, 0);
  }, [conversations]);

  const handleNewMessage = ({ data: message }) => {
    appendMessageFor(message.user, message);
    mutateConversationFor(message.user, (record) => ({
      ...record,
      lastResponse: message,
    }));
  };

  const handleUserUpdate = ({ data: user }) => {
    mutateConversationFor(user.id, (record) => ({
      ...record,
      with: { ...record.with, ...user },
    }));
  };

  useEffect(() => {
    conversationsSocket.on('message:new', handleNewMessage);
    conversationsSocket.on('user:update', handleUserUpdate);

    return () => {
      conversationsSocket.off('message:new', handleNewMessage);
      conversationsSocket.off('user:update', handleUserUpdate);
      conversationsSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!currentUser?.talentProfile?.available || !conversations) {
      return conversationsSocket.disconnect();
    }

    conversationsSocket.connect({ token: getCookie('fem_token') });
  }, [currentUser, conversationsSocket]);

  return (
    <ConversationContext.Provider
      value={{
        conversations,
        activeConversation,
        appendMessageFor,
        markAsRead,
        messages,
        mutateConversationFor,
        recipient: recipient || activeConversation?.with,
        sendMessageFor,
        unreadCount,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};
